import { camelToKebabCase, MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { produce } from 'immer';

import { ProjectContext } from '#core/contexts/ProjectContext.ts';
import { type ProjectConfigInput, ProjectConfigSchema } from '#core/schema/ProjectConfigSchema.ts';

export interface ProjectProviderProps<ConfigType extends ProjectConfigInput> {
  config: ConfigType;
  /** Selector Mantine attaches its CSS variables to. Set to the chat root class when rendering into a shadow root. */
  cssVariablesSelector?: string;
  /** Element Mantine sets color-scheme attributes on. Set when rendering into a shadow root. */
  getRootElement?: () => HTMLElement | undefined;
  /** Default target for all portalled elements (modals, popovers, tooltips). Set when rendering into a shadow root so floating elements stay inside the styled shadow tree. */
  portalTarget?: HTMLElement;
}

export function ProjectProvider<const ConfigType extends ProjectConfigInput>({
  children,
  config: inputConfig,
  cssVariablesSelector,
  getRootElement,
  portalTarget,
}: React.PropsWithChildren<ProjectProviderProps<ConfigType>>) {
  const config = ProjectConfigSchema.parse(inputConfig);
  const theme = produce(config?.theme ?? {}, (draft) => {
    if (!portalTarget) {
      return;
    }

    draft.components = {
      ...draft.components,
      Portal: {
        ...draft.components?.Portal,
        defaultProps: {
          ...draft.components?.Portal?.defaultProps,
          target: portalTarget,
        },
      },
    };
  });

  return (
    // @ts-expect-error - Config type is inferred from app's module augmentation
    <ProjectContext value={{ config }}>
      <MantineProvider
        cssVariablesResolver={(theme) => ({
          variables: Object.fromEntries(getThemeVariables(theme.other ?? {}, null)),
          light: {},
          dark: {},
        })}
        cssVariablesSelector={cssVariablesSelector}
        defaultColorScheme={config?.defaultColorScheme}
        forceColorScheme={config?.forceColorScheme}
        getRootElement={getRootElement}
        theme={theme}
      >
        <DatesProvider settings={{ locale: 'en', consistentWeeks: true }}>{children}</DatesProvider>
      </MantineProvider>
    </ProjectContext>
  );
}

function getThemeVariables(obj: Record<string, unknown>, prefix: string | null = '--mantine'): [string, string][] {
  return Object.entries(obj).flatMap(([key, value]) => {
    const kebabKey = camelToKebabCase(key);
    const variableName = prefix === null ? `--${kebabKey}` : `${prefix}-${kebabKey}`;

    if (Array.isArray(value)) {
      return getThemeVariables(Object.fromEntries(value.map((v, i) => [i, v])), variableName);
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      return getThemeVariables(value as Record<string, unknown>, variableName);
    }

    return [[variableName, (value ?? '').toString()]];
  });
}
